import { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ are one level deeper than src/pages/, requiring three levels up to reach convex/_generated/
import { useOrgContext } from '@/hooks/useOrgContext';
import { usePolicyFilters } from '@/hooks/usePolicyFilters';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Send } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { NotificationStatusPopover } from '@/components/insurance/NotificationStatusPopover';
import { PaymentSnoozePopover } from '@/components/insurance/PaymentSnoozePopover';
import type { IInsurancePolicy, IPaymentTracking, TInvoiceStatus } from '@/@types/insurance';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: same as above

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toPolicyList(
  raw:
    | {
        _id: IInsurancePolicy['_id'];
        orgId: IInsurancePolicy['orgId'];
        insuranceTypeId: IInsurancePolicy['insuranceTypeId'];
        policyNumber: string;
        customerName?: string;
        createdBy: IInsurancePolicy['createdBy'];
        creatorName?: string;
        status: IInsurancePolicy['status'];
        insuranceTypeName?: string;
        issuedAt?: number;
        expiresAt?: number;
        invoiceStatus?: TInvoiceStatus;
        createdAt: number;
        updatedAt: number;
      }[]
    | undefined
): IInsurancePolicy[] {
  if (!raw) return [];
  return raw.map(p => ({ ...p, fieldValues: [] }));
}

function formatDate(ts: number | undefined): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function invoiceStatusBadgeVariant(
  status: TInvoiceStatus | undefined
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'paid') return 'default';
  if (status === 'partially_paid') return 'secondary';
  if (status === 'not_paid') return 'destructive';
  return 'outline';
}

function invoiceStatusLabel(status: TInvoiceStatus | undefined): string {
  if (status === 'paid') return 'Paid';
  if (status === 'partially_paid') return 'Partially paid';
  if (status === 'not_paid') return 'Not paid';
  return 'Unknown';
}

/** Filter to policies that are unpaid / partially paid / unknown AND past grace period. */
function getPaymentPolicies(
  policies: IInsurancePolicy[],
  gracePeriodDays: number
): IInsurancePolicy[] {
  const now = Date.now();
  const graceMs = gracePeriodDays * 86_400_000;
  const unpaidStatuses: TInvoiceStatus[] = ['unknown', 'not_paid', 'partially_paid'];
  return policies.filter(p => {
    if (p.status !== 'active') return false;
    const status: TInvoiceStatus = p.invoiceStatus ?? 'unknown';
    if (!unpaidStatuses.includes(status)) return false;
    const issuedAt = p.issuedAt ?? p.createdAt;
    return now - issuedAt >= graceMs;
  });
}

/** Map IPaymentTracking to the shape IRenewalTracking expects for NotificationStatusPopover. */
function toNotificationTracking(t: IPaymentTracking | undefined) {
  if (!t) return null;
  return {
    _id: t._id as unknown as Id<'renewalTracking'>, // AS_JUSTIFIED: popover only reads notification fields, _id not used inside component
    policyId: t.policyId,
    orgId: t.orgId,
    renewalStatus: 'unknown' as const,
    emailSentAt: t.emailSentAt,
    emailError: t.emailError,
    emailMessageId: t.emailMessageId,
    smsSentAt: t.smsSentAt,
    smsError: t.smsError,
    smsBatchId: t.smsBatchId,
    companyEmailSentAt: t.companyEmailSentAt,
    companyEmailError: t.companyEmailError,
    createdAt: t.createdAt,
    updatedAt: t.updatedAt,
  };
}

// ---------------------------------------------------------------------------
// Sub-component: Payments table
// ---------------------------------------------------------------------------

interface IPaymentsTableProps {
  // LOCAL_TYPE_JUSTIFIED: private sub-component props used only in this file
  policies: IInsurancePolicy[];
  trackingByPolicy: Map<string, IPaymentTracking>;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleAll: () => void;
  onRowClick: (policyId: string) => void;
}

function PaymentsTable({
  policies,
  trackingByPolicy,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  onRowClick,
}: IPaymentsTableProps) {
  if (policies.length === 0) return null;

  const allSelected = policies.length > 0 && policies.every(p => selectedIds.has(p._id));
  const someSelected = policies.some(p => selectedIds.has(p._id)) && !allSelected;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">
            <Checkbox
              checked={allSelected ? true : someSelected ? 'indeterminate' : false}
              onCheckedChange={() => onToggleAll()}
              aria-label="Select all policies"
            />
          </TableHead>
          <TableHead>Policy #</TableHead>
          <TableHead>Customer</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Issued</TableHead>
          <TableHead>Invoice Status</TableHead>
          <TableHead>Partial Amount</TableHead>
          <TableHead>Snooze</TableHead>
          <TableHead>Notifications</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {policies.map(policy => {
          const tracking = trackingByPolicy.get(policy._id);
          return (
            <TableRow
              key={policy._id}
              className="cursor-pointer"
              onClick={() => onRowClick(policy._id)}
            >
              <TableCell onClick={e => e.stopPropagation()}>
                <Checkbox
                  checked={selectedIds.has(policy._id)}
                  onCheckedChange={() => onToggleSelect(policy._id)}
                  aria-label={`Select policy ${policy.policyNumber}`}
                />
              </TableCell>
              <TableCell className="font-mono text-sm">{policy.policyNumber}</TableCell>
              <TableCell className="font-medium">{policy.customerName ?? '—'}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {policy.insuranceTypeName ?? '—'}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(policy.issuedAt)}
              </TableCell>
              <TableCell>
                <Badge
                  variant={invoiceStatusBadgeVariant(policy.invoiceStatus)}
                  className="whitespace-nowrap"
                >
                  {invoiceStatusLabel(policy.invoiceStatus)}
                </Badge>
              </TableCell>
              <TableCell className="text-sm">
                {policy.invoiceStatus === 'partially_paid'
                  ? (policy as IInsurancePolicy & { partiallyPaidAmount?: number })
                      .partiallyPaidAmount != null // AS_JUSTIFIED: partiallyPaidAmount exists in schema but not yet in IInsurancePolicy interface
                    ? `€${((policy as IInsurancePolicy & { partiallyPaidAmount?: number }).partiallyPaidAmount ?? 0).toFixed(2)}` // AS_JUSTIFIED: same as above
                    : '—'
                  : '—'}
              </TableCell>
              <TableCell onClick={e => e.stopPropagation()}>
                {tracking ? (
                  <PaymentSnoozePopover
                    trackingId={tracking._id}
                    policyId={policy._id}
                    orgId={policy.orgId}
                    currentSnooze={
                      tracking.snoozedUntil
                        ? { snoozedUntil: tracking.snoozedUntil, snoozeNote: tracking.snoozeNote }
                        : undefined
                    }
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell onClick={e => e.stopPropagation()}>
                <NotificationStatusPopover tracking={toNotificationTracking(tracking)} />
              </TableCell>
              <TableCell onClick={e => e.stopPropagation()}>{/* Batch actions TBD */}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function PaymentsListPage() {
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const navigate = useNavigate();

  const [isSending, setIsSending] = useState(false);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const triggerManualSend = useMutation(api.insurance.paymentCron.triggerManualPaymentSend);

  const rawPolicies = useQuery(
    api.insurance.policies.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const trackingData = useQuery(
    api.insurance.paymentTracking.listByOrg,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const gracePeriodDays = useQuery(
    api.insurance.paymentSettings.getGracePeriodDays,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const isLoading = orgLoading || rawPolicies === undefined || gracePeriodDays === undefined;
  const allPolicies = toPolicyList(rawPolicies ?? undefined);
  const grace = gracePeriodDays ?? 14;

  const trackingByPolicy = useMemo(() => {
    const map = new Map<string, IPaymentTracking>();
    if (trackingData) {
      for (const t of trackingData) {
        map.set(t.policyId, t as IPaymentTracking); // AS_JUSTIFIED: query returns matching shape
      }
    }
    return map;
  }, [trackingData]);

  const paymentPolicies = useMemo(
    () => getPaymentPolicies(allPolicies, grace),
    [allPolicies, grace]
  );

  const { filters, filteredPolicies, dispatchFilter } = usePolicyFilters(paymentPolicies);

  const displayedPolicies = useMemo(() => {
    if (filters.invoiceFilter === 'all') return filteredPolicies;
    return filteredPolicies.filter(p => (p.invoiceStatus ?? 'unknown') === filters.invoiceFilter);
  }, [filteredPolicies, filters.invoiceFilter]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setSelectedIds(prev => {
      const allSelected = displayedPolicies.every(p => prev.has(p._id));
      if (allSelected) return new Set<string>();
      return new Set(displayedPolicies.map(p => p._id));
    });
  }, [displayedPolicies]);

  const handleRowClick = useCallback(
    (policyId: string) => {
      navigate(`/insurance/policies/${policyId}`);
    },
    [navigate]
  );

  const handleSendNotifications = useCallback(async () => {
    if (!currentOrg) return;
    setIsSending(true);
    try {
      await triggerManualSend({ orgId: currentOrg._id });
      toast.success('Payment notifications are being sent');
    } catch {
      toast.error('Failed to trigger notifications');
    } finally {
      setIsSending(false);
    }
  }, [triggerManualSend, currentOrg]);

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <CreditCard className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to view payment tracking.
          </p>
        </div>
      </div>
    );
  }

  const hasFilters =
    !!filters.searchQuery || filters.invoiceFilter !== 'all' || filters.typeFilter !== 'all';

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Payments</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading
              ? 'Loading...'
              : `${displayedPolicies.length} ${displayedPolicies.length === 1 ? 'policy' : 'policies'} with outstanding payments`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {currentOrg && (
            <Button
              disabled={isSending || displayedPolicies.length === 0}
              onClick={() => void handleSendNotifications()}
            >
              <Send aria-hidden="true" className="w-4 h-4 mr-2" />
              {isSending ? 'Sending...' : 'Send Notifications'}
            </Button>
          )}
        </div>
      </div>

      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-3 border-b border-table-row-border">
          <label htmlFor="payment-search" className="sr-only">
            Search policies
          </label>
          <Input
            id="payment-search"
            placeholder="Search by policy # or customer..."
            value={filters.searchQuery}
            onChange={e => dispatchFilter({ type: 'SET_SEARCH', query: e.target.value })}
            className="max-w-xs"
          />
          <label htmlFor="payment-invoice-filter" className="sr-only">
            Filter by invoice status
          </label>
          <Select
            value={filters.invoiceFilter}
            onValueChange={val =>
              dispatchFilter({ type: 'SET_INVOICE_FILTER', invoiceFilter: val })
            }
          >
            <SelectTrigger id="payment-invoice-filter" className="w-[180px]">
              <SelectValue placeholder="All invoices" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All invoices</SelectItem>
              <SelectItem value="not_paid">Not paid</SelectItem>
              <SelectItem value="partially_paid">Partially paid</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-auto">
          {!isLoading && displayedPolicies.length > 0 && (
            <PaymentsTable
              policies={displayedPolicies}
              trackingByPolicy={trackingByPolicy}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onToggleAll={handleToggleAll}
              onRowClick={handleRowClick}
            />
          )}
          {!isLoading && displayedPolicies.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CreditCard
                className={cn(
                  'w-12 h-12 mb-4',
                  hasFilters ? 'text-muted-foreground' : 'text-green-500'
                )}
              />
              <h3 className="text-lg font-medium text-foreground mb-1">
                {hasFilters ? 'No matching policies' : 'All caught up!'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {hasFilters
                  ? 'Try adjusting your filters or search query'
                  : `No active policies have outstanding payments past the ${grace}-day grace period`}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
